import React from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

interface PaginationProps {
  currentPage: number;
  pageLimit: number;
  totalPages: number;
  totalRecords?: number;
  onPageChange?: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  pageLimit,
  totalPages,
  totalRecords = 0,
  onPageChange,
}) => {
  const startRecord = (currentPage - 1) * pageLimit + 1;
  const endRecord = Math.min(currentPage * pageLimit, totalRecords);
  const pages = (() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const items: Array<number | "ellipsis"> = [1];
    let left = Math.max(2, currentPage - 1);
    let right = Math.min(totalPages - 1, currentPage + 1);

    if (currentPage <= 3) {
      left = 2;
      right = 4;
    } else if (currentPage >= totalPages - 2) {
      left = totalPages - 3;
      right = totalPages - 1;
    }

    if (left > 2) {
      items.push("ellipsis");
    }

    for (let i = left; i <= right; i += 1) {
      items.push(i);
    }

    if (right < totalPages - 1) {
      items.push("ellipsis");
    }

    items.push(totalPages);
    return items;
  })();

  return (
    <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-4 text-sm">
      <p className="text-gray-500">
        Showing {totalRecords === 0 ? 0 : startRecord} to {endRecord} of{" "}
        {totalRecords}
      </p>

      <div className="flex items-center space-x-1">
        <button
          disabled={currentPage === 1}
          onClick={() => onPageChange?.(1)}
          className="p-2 border rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          <ChevronsLeft size={16} />
        </button>
        <button
          disabled={currentPage === 1}
          onClick={() => onPageChange?.(currentPage - 1)}
          className="p-2 border rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          <ChevronLeft size={16} />
        </button>

        {pages.map((item, i) =>
          item === "ellipsis" ? (
            <span key={`e-${i}`} className="px-2 text-gray-400">
              ...
            </span>
          ) : (
            <button
              key={item}
              onClick={() => onPageChange?.(item)}
              className={`w-8 h-8 rounded-md text-sm font-medium ${
                currentPage === item
                  ? "bg-indigo-600 text-white"
                  : "border hover:bg-gray-50"
              }`}
            >
              {item}
            </button>
          )
        )}

        <button
          disabled={currentPage === totalPages}
          onClick={() => onPageChange?.(currentPage + 1)}
          className="p-2 border rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          <ChevronRight size={16} />
        </button>
        <button
          disabled={currentPage === totalPages}
          onClick={() => onPageChange?.(totalPages)}
          className="p-2 border rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          <ChevronsRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
